import _ from 'lodash';

import moment from 'moment';
import AssetService from './asset-service';
import ValidationService from './validation-service';
import TranslationService from './translation-service';
import UploadService from './upload-service';
import RouteService from './route-service';
import IdService from './id-service';
import api from './api-service';
import { captureError } from '../pixwel/exception-handler.factory';
import LanguageService from './language-service';
import {
	getTranscriptsForOrder, graphicsNeedsApproval, isNoTranslationNeeded, needsPrintTranslation,
	offersSplitTranslators
} from '~/utils/orders';
import { USAGE_OPTIONS } from '~/constants';

const THEATRICAL_USAGE_OPTIONS = [
	...USAGE_OPTIONS.theatrical,
	...USAGE_OPTIONS.theatrical3D,
	...USAGE_OPTIONS.theatricalIMAX,
	...USAGE_OPTIONS.theatrical3DIMAX,
	...USAGE_OPTIONS.theatrical2DAtmos,
	...USAGE_OPTIONS.theatrical3DAtmos,
	...USAGE_OPTIONS.theatrical2DDolbyVision,
	...USAGE_OPTIONS.theatrical3DDolbyVision
].reduce((result, option) => {
	result[option.value] = false;
	return result;
}, {});

function createTheatricalUsage(future) {
	return {
		sendTo: [],
		deliveryDeadline: {
			dateTime: future
		},
		shipping: {
			qty: 0,
			addresses: []
		},
		...THEATRICAL_USAGE_OPTIONS
	};
}

function createTheatricalUsageWithoutDeadline() {
	return {
		sendTo: [],
		shipping: {
			qty: 0,
			addresses: []
		},
		...THEATRICAL_USAGE_OPTIONS
	};
}

const ROADSHOW_REQUIRED = {
	if: {
		condition: (value, { meta }) => {
			return AssetService.isRoadshowAsset(meta.asset);
		},
		validations: {
			presence: {
				allowEmpty: false
			}
		}
	}
};
// Usage section keys that hold metadata rather than a deliverable selection.
const USAGE_RESERVED_KEYS = ['sendTo', 'shipping', 'deliveryDeadline'];
const USAGE_REQUIRED = {
	custom: {
		fn: (value, { fullObject }) => {
			// We can check all usage sections instead of the asset specific ones
			// because a selection can only come from a section the form rendered:
			// workflow default deliverables are intersected with the options
			// available for the asset before they are applied, and selections are
			// pruned again whenever the available options change (see
			// restrictDeliverablesToAvailable).
			let valid = _.reduce(fullObject.usage, (result, usageOptions) => {
				return result || _.reduce(usageOptions, (result, value, key) => {
					if( ['sendTo', 'shipping', 'deliveryDeadline'].indexOf(key) >= 0 ) {
						return result;
					}

					return result || value;
				}, result);
			}, false);

			return valid ? undefined : 'Must select at least one usage';
		}
	}
};
/**
 * Whether the order form shows (and therefore requires) a translation provider.
 *
 * This must stay in sync with the `renderTranslationProvider` gate in the
 * localization section — if the picker is on screen, its value is required.
 */
const needsTranslationProvider = (order, asset) => {
	const localization = order?.localization ?? {};

	// Autosubs and audio-only orders translate without a provider.
	if( localization.autosubs || localization.auto === 'audio' ) {
		return false;
	}

	// A print order keeps DNG at OV — its translation choice is printTranslation,
	// and a date or full one may be handed to a vendor like any other order
	// (PLATFORM-4185).
	if( AssetService.isPrintAsset(asset ?? {}) ) {
		return needsPrintTranslation(localization);
	}

	// Roadshow translation is independent of DNG — check it on its own so
	// isNoTranslationNeeded (which only covers DNG) can't mask it.
	if( AssetService.isRoadshowAsset(asset ?? {}) ) {
		return localization.roadshowTranslation === 'Subtitled';
	}

	// Covers Subtitled dialogue/narration/graphics *and* Dedicated / Localized
	// graphics, which also needs a translation of the graphics.
	return !isNoTranslationNeeded(order);
};

/* A dual-language workflow order names one translator per language instead
*  of a single vendor: the per-language pickers replace the single one.
*
*  Print is excluded: the print subtitler works the order's single translation
*  document (`translation.printLines` for full, `translation.date` for date) and
*  never a per-language one, so a vendor named per language would be given a
*  document nothing reads (PLATFORM-4185).
* */
const usesPerLanguageTranslators = (order, asset) => !!asset?.workflow?.id
	&& !AssetService.isPrintAsset(asset ?? {})
	&& LanguageService.isDualLanguage(order.localization.language);

/* One translator for each of the order's languages, no more, no less. */
const validateMultiTranslations = (value, { meta }) => {
	const entries = Array.isArray(value) ? value : [];
	const expectedLanguages = meta.order.localization.language?.split('-').filter(Boolean) ?? [];
	const entriesWithLanguage = entries.filter((entry) => !!entry?.language);
	const entriesByLanguage = new Map(
		entriesWithLanguage
			.filter((entry) => !!entry?.translator)
			.map((entry) => [entry.language, entry])
	);
	const hasNoEmptyLanguages = entriesWithLanguage.length === entries.length;
	const hasNoDuplicates = (new Set(entriesWithLanguage.map((entry) => entry.language)))
		.size === entriesWithLanguage.length;
	const hasOnlyExpectedLanguages = entriesWithLanguage
		.every((entry) => expectedLanguages.includes(entry.language));
	const hasTranslatorForEachLanguage = expectedLanguages
		.every((language) => !!entriesByLanguage.get(language)?.translator);
	return hasNoEmptyLanguages && hasNoDuplicates
	&& hasOnlyExpectedLanguages && hasTranslatorForEachLanguage
		? undefined
		: 'must have a translator for each language';
};

export const ORDER_CONSTRAINTS = {
	due: {
		dateTime: {
			if: {
				condition: (value, { meta }) => {
					const { order } = meta;
					return !(order.localization.autosubs || order.localization.auto);
				},
				validations: {
					presence: true,
					datetime: {
						earliest: moment.utc().add(1, 'day'),
						tooEarly: 'must be at least 24 hours from now'
					}
				}
			}
		}
	},
	localization: {
		language: {
			presence: {
				allowEmpty: false
			}
		},
		territory: {
			presence: {
				allowEmpty: false
			}
		},
		audioFile: {
			if: {
				condition: (value, { meta }) => {
					const { order } = meta;
					return order.localization.auto === 'audio';
				},
				validations: {
					presence: {
						allowEmpty: false
					}
				}
			}
		},
		translationProviderType: {
			presence: {
				allowEmpty: false
			}
		},
		translationProvider: {
			if: {
				condition: (value, { meta }) => {
					const { order, asset } = meta;

					if( !needsTranslationProvider(order, asset) ) {
						return false;
					}

					return order.localization.translationProviderType === 'email'
						&& !usesPerLanguageTranslators(order, asset);
				},
				validations: {
					presence: {
						allowEmpty: false
					}
				}
			}
		},
		graphicsTranslationProviderType: {
			presence: {
				allowEmpty: false
			}
		},
		// The graphics side only has a picker of its own on a split order; on
		// every other order the single translator covers graphics too.
		graphicsTranslationProvider: {
			if: {
				condition: (value, { meta }) => {
					const { order, asset } = meta;

					return needsTranslationProvider(order, asset)
						&& offersSplitTranslators(order, asset)
						&& order.localization.graphicsTranslationProviderType === 'email'
						&& !usesPerLanguageTranslators(order, asset);
				},
				validations: {
					presence: {
						allowEmpty: false
					}
				}
			}
		},
		// The graphics side of a split dual-language order names a graphics
		// translator per language, just as the subtitle side does.
		graphicsMultiTranslations: {
			if: {
				condition: (value, { meta }) => {
					const { order, asset } = meta;
					return needsTranslationProvider(order, asset)
						&& offersSplitTranslators(order, asset)
						&& order.localization.graphicsTranslationProviderType === 'email'
						&& usesPerLanguageTranslators(order, asset);
				},
				validations: {
					custom: {
						fn: validateMultiTranslations
					}
				}
			}
		},
		graphicsTranslationFile: {
			if: {
				condition: (value, { meta }) => {
					const { order, asset } = meta;

					return needsTranslationProvider(order, asset)
						&& offersSplitTranslators(order, asset)
						&& order.localization.graphicsTranslationProviderType === 'upload';
				},
				validations: {
					presence: {
						allowEmpty: false
					}
				}
			}
		},
		multiTranslations: {
			if: {
				condition: (value, { meta }) => {
					const { order, asset } = meta;
					return needsTranslationProvider(order, asset)
						&& order.localization.translationProviderType === 'email'
						&& usesPerLanguageTranslators(order, asset);
				},
				validations: {
					custom: {
						fn: validateMultiTranslations
					}
				}
			}
		},
		translationFile: {
			if: {
				condition: (value, { meta }) => {
					const { order, asset } = meta;

					if( !needsTranslationProvider(order, asset) ) {
						return false;
					}

					return order.localization.translationProviderType === 'upload';
				},
				validations: {
					presence: {
						allowEmpty: false
					}
				}
			}
		}
	},
	tags: {
		tags: {
			if: {
				condition: (value, { meta }) => {
					let { order, asset } = meta;
					return !(order.localization.autosubs || order.localization.auto)
						&& !AssetService.isPrintAsset(asset)
						&& !AssetService.isRoadshowAsset(asset)
						&& !asset.workflow?.phase;
				},
				validations: {
					presence: {
						allowEmpty: false,
						message: 'must have at least 1 tag'
					},
					arrayOf: {
						tag: {
							presence: {
								allowEmpty: false
							},
							unique: {
								getAllValues: (value, { fullObject }) => {
									return Object.values(fullObject.tags)?.map((tag) => tag.tag) ?? [];
								},
								compareFn: (v1, v2) => {
									return v1 === v2;
								},
								findDuplicates: (tags) => {
									return (new Set(tags.map( (e) => e.tag.toLowerCase()))).size !== tags.length;
								}
							}
						}
					}
				}
			}
		}
	},
	usage: {
		broadcast: USAGE_REQUIRED,
		online: USAGE_REQUIRED,
		theatrical: USAGE_REQUIRED,
		print: USAGE_REQUIRED,
		digital: USAGE_REQUIRED,
		roadshow: USAGE_REQUIRED,
		streaming: USAGE_REQUIRED
	},
	questions: {
		eventName: ROADSHOW_REQUIRED,
		territory: ROADSHOW_REQUIRED,
		venueName: ROADSHOW_REQUIRED,
		venueAddress: ROADSHOW_REQUIRED,
		rehearsalDate: ROADSHOW_REQUIRED,
		eventDate: ROADSHOW_REQUIRED,
		attendeeDetails: ROADSHOW_REQUIRED
	},
	additionalInfo: {}
};

const WorkRequestOrderService = {

	createBlankOrder: () => {
		let future = new Date();
		future.setTime(future.getTime() + (48 * 60 * 60 * 1000));

		return {
			due: {
				dateTime: future
			},
			localization: {
				language: '',
				territory: '',
				autosubs: false,
				auto: false,
				dialogue: 'OV',
				narration: 'OV',
				graphics: 'OV',
				printTranslation: 'ov',
				roadshowTranslation: 'OV',
				translationProviderType: 'me',
				translationProvider: null,
				graphicsTranslationProviderType: 'me',
				graphicsTranslationProvider: null,
				graphicsTranslationFile: null,
				graphicsMultiTranslations: [],
				multiTranslations: [],
				translationFile: null,
				needsApproval: false,
				graphicsNeedsApproval: false,
				dubbed: false,
				audioFile: null
			},
			tags: {
				tags: [
					createTag()
				]
			},
			usage: {
				broadcast: {
					sendTo: [],
					'SD 4:3': false,
					'SD 16:9': false,
					'HD': false
				},
				online: {
					sendTo: [],
					'Orban': false,
					'1920p': false,
					'1350p': false,
					'1080p': false,
					'720p': false,
					'480p': false,
					'360p': false
				},
				theatrical: createTheatricalUsage(future),
				print: {
					sendTo: [],
					cmyk: false
				},
				digital: {
					sendTo: [],
					rgb: false
				},
				roadshow: {
					sendTo: [],
					physical: false,
					aspera: false
				},
				streaming: {
					sendTo: [],
					streamingfile: false
				}
			},
			questions: {
				eventName: '',
				territory: '',
				venueName: '',
				venueAddress: '',
				rehearsalDate: null,
				eventDate: null,
				attendeeDetails: '',
				securityDetails: '',
				notes: ''
			},
			additionalInfo: {
				specialInstructions: '',
				files: []
			}
		};
	},

	// Recompute the usage deliverable selections from workflow-level default
	// deliverables ({ broadcast: [...], online: [...], theatrical: [...] }),
	// keeping only deliverables present in availableOptions (the options the
	// work request form actually renders for the asset) so a default can never
	// select something the user cannot see or remove. Reserved keys (sendTo,
	// shipping, deliveryDeadline) are preserved as-is.
	applyDefaultDeliverables: (usage, defaults, availableOptions) => {
		return _.mapValues(usage, (section, usageKey) => {
			const availableValues = (availableOptions?.[usageKey] ?? []).map((option) => option.value);
			const selected = (Array.isArray(defaults?.[usageKey]) ? defaults[usageKey] : [])
				.filter((deliverable) => availableValues.includes(deliverable));

			return _.mapValues(section, (value, key) => {
				if( USAGE_RESERVED_KEYS.includes(key) ) {
					return value;
				}

				return selected.includes(key);
			});
		});
	},

	// Deselect any deliverable that is no longer in availableOptions (e.g. the
	// theatrical section hidden once an order becomes auto/autosubs) while
	// keeping every selection that is still selectable. Used when the user has
	// edited the usage section so defaults must not be re-applied, but hidden
	// selections must still never satisfy validation or be submitted.
	restrictDeliverablesToAvailable: (usage, availableOptions) => {
		return _.mapValues(usage, (section, usageKey) => {
			const availableValues = (availableOptions?.[usageKey] ?? []).map((option) => option.value);

			return _.mapValues(section, (value, key) => {
				if( USAGE_RESERVED_KEYS.includes(key) ) {
					return value;
				}

				return availableValues.includes(key) ? value : false;
			});
		});
	},

	validateOrder: (order, asset) => {
		let options = {
			meta: {
				order,
				asset
			}
		};

		return ValidationService.validateNested(order, ORDER_CONSTRAINTS, options);
	},

	wrapOrderWithValidations: (order, asset) => {
		let validations = WorkRequestOrderService.validateOrder(order, asset);

		let wrappedOrder = {};
		Object.entries(order||{})?.forEach(([section, orderSection]) => {
			// If the section is simple data type (i.e. not an object)
			// then we skip validation. This is probably meta information like _id
			if( !_.isObject(orderSection) ) {
				return;
			}

			wrappedOrder[section] = wrapFields(orderSection, validations[section], BLANK_ORDER[section]);
		});

		return wrappedOrder;

		function wrapFields(section, sectionValidation, BLANKS) {
			let fields = {};

			Object.entries(section||{})?.forEach(([field, fieldValue]) => {
				let fieldValidation = _.get(sectionValidation, field);
				let isArray = _.isArray(fieldValue);

				let wrappedField = isArray ? [] : {};
				if(field === 'theatrical') {
					wrappedField.isPristine = _.isEqual(
						{ ...fieldValue, deliveryDeadline: '' },
						{ ...BLANKS[field], deliveryDeadline: '' }
					);
				}
				else {
					wrappedField.isPristine = _.isEqual(fieldValue, BLANKS[field]);
				}
				wrappedField.isInvalid = !_.isEmpty(fieldValidation);
				wrappedField.value = fieldValue;
				wrappedField.validations = fieldValidation;

				if( isArray ) {
					let arrayOfValidations = _.get(fieldValidation, 'arrayOf');

					fieldValue?.forEach((element, i) => {
						let elementValidation = _.get(arrayOfValidations, i);
						let wrappedElement = elementValidation
							? wrapFields(element, elementValidation, BLANK_PARTIALS[field])
							: undefined;

						wrappedField.push(wrappedElement);
					});
				}

				fields[field] = wrappedField;
			});

			return fields;
		}
	},

	doesOrderMissingTranscripts: (order, asset) => {
		const dng = typeof order.dng === 'string'
			? order.dng
			: {
				dialogue: order.dng?.dialogue ?? [order.localization?.dialogue],
				graphics: order.dng?.graphics ?? [order.localization?.graphics],
				narration: order.dng?.narration ?? [order.localization?.narration]
			};
		const workRequestLikeOrder = {
			dng,
			auto: order.auto ?? !!order.localization?.auto,
			autosubs: order.autosubs ?? !!order.localization?.autosubs,
			asset
		};
		const transcripts = getTranscriptsForOrder(workRequestLikeOrder);

		const needsMainTranscription = transcripts._id && !asset?.transcription?._id;
		const needsDialogueTranscription = transcripts.dialogue && !asset?.transcription?.dialogue;
		const needsGraphicsTranscription = (transcripts.graphics && !asset?.transcription?.graphics)
			|| (transcripts.autogfx && !asset?.transcription?.autogfx);

		return needsMainTranscription || needsDialogueTranscription || needsGraphicsTranscription;
	},

	getCostEstimate: (order, asset, assetType) => {
		let workRequest = WorkRequestOrderService.toWorkRequest(order, asset, assetType);

		return api.client.post('/workrequests/estimate', workRequest)
			.then((estimate) => _.pick(estimate.data, ['applied', 'base', 'errors']));
	},

	doesOrderNeedTranslation: (order) => {
		const {
			dialogue,
			narration,
			graphics,
			printTranslation,
			roadshowTranslation
		} = order.localization;

		const isPrintTranslation = ['date', 'full']
			.includes(printTranslation);

		// Roadshow translation is independent of DNG — check it first
		// so isNoTranslationNeeded (which only covers DNG) can't mask it.
		if(roadshowTranslation === 'Subtitled') {
			return true;
		}

		const isAvTranslation = [dialogue, narration, graphics]
			.includes('Subtitled') || [graphics].includes('Dedicated / Localized');

		if(isNoTranslationNeeded(order)) {
			return false;
		}

		return isPrintTranslation || isAvTranslation;
	},

	doesGfxOrderNeedTranslation: (order, asset) => {
		if(isNoTranslationNeeded(order)) {
			return false;
		}

		return ['Dedicated / Localized', 'Subtitled'].includes(order.localization?.graphics)
			&& order.localization?.dialogue !== 'Subtitled'
			&& order.localization?.narration !== 'Subtitled'
			&& !!asset.transcription?.graphics;
	},

	toWorkRequest: (order, asset, assetType) => {
		let isPrintAsset = AssetService.isPrintAsset(asset);
		let isRoadshowAsset = AssetService.isRoadshowAsset(asset);

		if( order.localization.autosubs || order.localization.auto ) {
			order.usage.theatrical = BLANK_ORDER.usage.theatrical;
		}

		let transformedOrder = {
			asset: asset._id,
			assetType: asset?.type,
			assetCategory: assetType?.category,
			due: {
				date: order.due.dateTime,
				tz: Intl.DateTimeFormat().resolvedOptions().timeZone
			},
			deliveryDeadline: {
				date: order.usage['theatrical']?.deliveryDeadline?.dateTime,
				tz: Intl.DateTimeFormat().resolvedOptions().timeZone
			},
			auto: !!order.localization.auto,
			autoMode: order.localization.auto,
			autosubs: order.localization.autosubs,
			language: order.localization.language,
			territory: order.localization.territory,
			dng: {
				dialogue: [order.localization.dialogue],
				graphics: [order.localization.graphics],
				narration: [order.localization.narration]
			},
			audioFile: order.localization.dubbed ? order.localization.audioFile : null,
			instructions: order.additionalInfo.specialInstructions,
			urls: _.mapValues({
				view: RouteService.stateHref('work-request', { id: ':workRequest' }, { absolute: true }),
				manage: RouteService.stateHref('work-request', { id: ':workRequest' }, { absolute: true }),
				invite: RouteService.stateHref('register', { code: ':code' }, { absolute: true }),
				translate: RouteService.stateHref('subtitler', { id: ':workRequest' }, { absolute: true })
			}, decodeURIComponent)
		};

		// If this order has an id, assign it to work request
		if( order._id ) {
			transformedOrder._id = order._id;
		}

		// An order that needs no translation at all names no vendor. The pickers
		// are hidden rather than cleared, so a contact chosen before the choice
		// changed — a print order switched back to OV, DNG set to OV — is still
		// on the form; sending it would grant that vendor access to the work
		// request through `translator` (PLATFORM-4185).
		const hasTranslationVendor = needsTranslationProvider(order, asset);

		// A translator may be a User object (existing user) or a plain email string
		// (new user). Send either the user id or the email string so the API can
		// provision the translation record up-front; for emails, the API's
		// NormalizeAndInvite filter will resolve the user id when the translation
		// is later updated.
		const translator = hasTranslationVendor
			? serializeTranslator(order.localization?.translationProvider)
			: null;
		if(translator) {
			transformedOrder.translator = translator;
		}

		// On a split order the graphics side names its own vendor. Left unset,
		// the API provisions graphics with `translator`, which is what every
		// other order wants.
		if(offersSplitTranslators(order, asset) && order.localization?.graphicsTranslationProviderType === 'email') {
			const graphicsTranslator = serializeTranslator(order.localization?.graphicsTranslationProvider);
			if(graphicsTranslator) {
				transformedOrder.graphicsTranslator = graphicsTranslator;
			}
		}

		// We must send a translator value (id or email) on creation so the API's
		// MultiTranslationSync can provision a translation record — without a
		// translator, no translation is created and the work request's
		// multiTranslations[i].translation remains undefined.
		const serializeMultiTranslations = (multiTranslations) => multiTranslations.map((translation) => ({
			language: translation.language,
			translator: serializeTranslator(translation.translator)
		}));

		// Not when the order names no vendor at all, and never for print — the
		// print subtitler works the order's single translation document, so a
		// per-language vendor would be provisioned one nothing reads.
		if(hasTranslationVendor
			&& !isPrintAsset
			&& order.localization?.multiTranslations?.length > 0
		) {
			transformedOrder.multiTranslations = serializeMultiTranslations(order.localization.multiTranslations);
		}

		// The graphics side of a split dual-language order names its own translator per
		// language — only while the order is one and the side is on a vendor, so contacts
		// left over from an earlier language or provider choice are never provisioned.
		if(offersSplitTranslators(order, asset)
			&& usesPerLanguageTranslators(order, asset)
			&& order.localization?.graphicsTranslationProviderType === 'email'
			&& order.localization?.graphicsMultiTranslations?.length > 0
		) {
			transformedOrder.graphicsMultiTranslations = serializeMultiTranslations(
				order.localization.graphicsMultiTranslations
			);
		}

		if( isPrintAsset ) {
			transformedOrder.dng = 'OV';
			transformedOrder.printTranslation = order.localization.printTranslation;
			transformedOrder.tags = [];

			let { uses, recipients, shipping } = transformUsage(_.pick(order.usage, ['print', 'digital']));
			transformedOrder.uses = uses;
			transformedOrder.recipients = recipients;
			transformedOrder.shipping = shipping;
		}
		else if( isRoadshowAsset ) {
			transformedOrder.dng = order.localization.roadshowTranslation;
			transformedOrder.questions = order.questions;

			let { uses, recipients } = transformUsage(_.pick(order.usage, ['roadshow', 'streaming']));
			transformedOrder.uses = uses;
			transformedOrder.recipients = recipients;
		}
		else {

			if( !(order.localization.autosubs || order.localization.auto)) {
				transformedOrder.tags = order.tags.tags?.map((tag) => ({
					name: tag.tag,
					description: tag.instructions,
					files: [] // Don't send tag files on WR creation, they are saved later using a PATCH request (see *placeOrder function)
				})) ?? [];
			}

			transformedOrder.files = [{
				name: 'attachments',
				files: []
			}];

			let { uses, dcp, recipients, shipping } = transformUsage(_.pick(order.usage, ['broadcast', 'online', 'theatrical']));
			transformedOrder.uses = uses;
			transformedOrder.dcp = dcp;
			transformedOrder.recipients = recipients;
			transformedOrder.shippingAddresses = shipping;
		}

		return transformedOrder;
	},

	fromWorkRequest: (workRequest, asset = workRequest.asset) => {
		let isPrintAsset = workRequest.assetCategory === 'print';
		let isRoadshowAsset = workRequest.assetType === 'roadshow';
		const isWorkflowAsset = workRequest.asset.workflow?.phase;

		let transformedWorkRequest = {
			_id: workRequest._id,
			due: {
				dateTime: moment.unix(workRequest.due.date).toDate()
			},
			localization: {
				language: workRequest.language,
				territory: workRequest.territory,
				autosubs: workRequest.autosubs,
				auto: workRequest.auto,
				autoMode: workRequest.autoMode,
				dialogue: 'OV',
				narration: 'OV',
				graphics: 'OV',
				printTranslation: 'ov',
				roadshowTranslation: 'OV',
				translationProviderType: 'me',
				translationProvider: null,
				graphicsTranslationProviderType: 'me',
				graphicsTranslationProvider: null,
				graphicsTranslationFile: null,
				graphicsMultiTranslations: [],
				multiTranslations: [],
				translationFile: null,
				needsApproval: false,
				graphicsNeedsApproval: false,
				dubbed: false,
				audioFile: null
			},
			usage: {
				// TODO - leave this as is, loop through and switch properties to true below
				broadcast: {
					sendTo: [],
					'SD 4:3': false,
					'SD 16:9': false,
					HD: false
				},
				online: {
					sendTo: [],
					Orban: false,
					'1920p': false,
					'1350p': false,
					'1080p': false,
					'720p': false,
					'480p': false,
					'360p': false
				},
				theatrical: createTheatricalUsageWithoutDeadline(),
				print: {
					sendTo: [],
					cmyk: false
				},
				digital: {
					sendTo: [],
					rgb: false
				},
				roadshow: {
					sendTo: [],
					physical: false,
					aspera: false
				},
				streaming: {
					sendTo: [],
					streamingfile: false
				}
			},
			questions: {
				eventName: '',
				territory: '',
				venueName: '',
				venueAddress: '',
				rehearsalDate: null,
				eventDate: null,
				attendeeDetails: '',
				securityDetails: '',
				notes: ''
			},
			additionalInfo: {
				specialInstructions: workRequest.instructions || '',
				files: workRequest.files
			}
		};

		if(workRequest.deliveryDeadline) {
			transformedWorkRequest.usage.theatrical.deliveryDeadline = {
				dateTime: moment(workRequest.deliveryDeadline.date).toDate()
			};
		}

		if( isPrintAsset ) {
			// localization
			transformedWorkRequest.localization.printTranslation = workRequest.printTranslation;

			// translation — a date/full print translation may be worked by a
			// vendor, so the order form reads back who it was assigned to
			if( workRequest.translation ) {
				transformedWorkRequest.localization = {
					...transformedWorkRequest.localization,
					translationProviderType: getTranslationProviderType(workRequest),
					translationProvider: IdService.convertToId(workRequest.translation.translator),
					needsApproval: !!workRequest.translation.approval
				};
			}

			// tags
			transformedWorkRequest.tags = [];

			// usage
			transformedWorkRequest.usage.print.cmyk = !!workRequest.uses.details.Cmyk;
			transformedWorkRequest.usage.digital.rgb = !!workRequest.uses.details.Rgb;

			// print recipients
			if( workRequest.recipients.Cmyk.length > 0 ) {
				transformedWorkRequest.usage.print.sendTo =
					IdService.convertToIds(workRequest.recipients.Cmyk);
			}

			// digital recipients
			if( workRequest.recipients.Rgb.length > 0 ) {
				transformedWorkRequest.usage.digital.sendTo =
					IdService.convertToIds(workRequest.recipients.Rgb);
			}
		}
		else if( isRoadshowAsset ) {
			// localization
			transformedWorkRequest.localization.roadshowTranslation = workRequest.dng;

			// translation
			if( workRequest.translation ) {
				transformedWorkRequest.localization = {
					...transformedWorkRequest.localization,
					translationProviderType: getTranslationProviderType(workRequest),
					translationProvider: IdService.convertToId(workRequest.translation.translator),
					multiTranslations: (workRequest.multiTranslations || []).map((translation) => ({
						language: translation.language,
						translator: IdService.convertToId(translation.translator)
					})),
					translationFile: workRequest.translation.file || null,
					needsApproval: workRequest.translation?.approval
						|| workRequest.graphicsTranslation?.approval
						|| false
				};
			}

			// usage
			if( workRequest.uses.details.Dcp ) {
				workRequest.uses.details.Dcp.forEach((use) => {
					transformedWorkRequest.usage.roadshow[use.value] = true;
				});
			}

			if( workRequest.uses.details.Streamingfile ) {
				workRequest.uses.details.Streamingfile.forEach((use) => {
					transformedWorkRequest.usage.streaming[use.value] = true;
				});
			}

			// recipients
			if( workRequest.recipients.Dcp.length > 0 ) {
				transformedWorkRequest.usage.roadshow.sendTo =
					IdService.convertToIds(workRequest.recipients.Dcp);
			}
			if( workRequest.recipients.Streamingfile.length > 0 ) {
				transformedWorkRequest.usage.streaming.sendTo =
					IdService.convertToIds(workRequest.recipients.Streamingfile);
			}

			// questions
			transformedWorkRequest.questions = workRequest.questions;
		}
		else {
			// localization
			transformedWorkRequest.localization = {
				...transformedWorkRequest.localization,
				dialogue: dngComponent(workRequest, 'dialogue'),
				narration: dngComponent(workRequest, 'narration'),
				graphics: dngComponent(workRequest, 'graphics')
			};

			// tags
			if( !(workRequest.autosubs || workRequest.auto) && !isWorkflowAsset ) {
				transformedWorkRequest.tags = {
					tags: workRequest.tags.map((tag) => ({
						tag: tag.name,
						instructions: tag.description || '',
						files: tag.files || []
					}))
				};
			}

			// translation
			if( workRequest.translation ) {
				transformedWorkRequest.localization = {
					...transformedWorkRequest.localization,
					translationProviderType: getTranslationProviderType(workRequest),
					translationProvider: IdService.convertToId(workRequest.translation.translator),
					multiTranslations: (workRequest.multiTranslations || []).map((translation) => ({
						language: translation.language,
						translator: IdService.convertToId(translation.translator)
					})),
					translationFile: workRequest.translation.file || null,
					needsApproval: workRequest.translation?.approval
						|| workRequest.graphicsTranslation?.approval
						|| false
				};
			}

			// graphics translation — its own vendor (or vendor per language) on a split order
			if( offersSplitTranslators(workRequest, asset)
				&& (workRequest.graphicsTranslation || workRequest.graphicsMultiTranslations?.length > 0)
			) {
				transformedWorkRequest.localization = {
					...transformedWorkRequest.localization,
					graphicsTranslationProviderType: getGraphicsTranslationProviderType(workRequest),
					graphicsTranslationProvider: IdService.convertToId(workRequest.graphicsTranslation?.translator),
					graphicsTranslationFile: workRequest.graphicsTranslation?.file || null,
					graphicsMultiTranslations: (workRequest.graphicsMultiTranslations || []).map((translation) => ({
						language: translation.language,
						translator: IdService.convertToId(translation.translator)
					})),
					// each side of a split order has an approval choice of its own
					needsApproval: !!workRequest.translation?.approval,
					graphicsNeedsApproval: !!workRequest.graphicsTranslation?.approval
				};
			}

			// broadcast/online usage
			Object.entries(workRequest?.uses?.details??{}).forEach(([category, usesArr]) => {
				// 'Broadcast' -> 'broadcast'
				let useCategory = category.toLowerCase();
				let useKey;

				usesArr.forEach((use) => {
					// 'Sd 4:3' -> 'SD 4:3'
					useKey = useCategory === 'broadcast' ? use.value.toUpperCase() : use.value;
					transformedWorkRequest.usage[useCategory][useKey] = true;
				});
			});

			// dcp usage
			Object.keys(workRequest?.dcp?.details||{}).forEach((key) => {
				let details = workRequest.dcp.details[key];
				let useCategory = 'theatrical';
				let useKey = key;
				transformedWorkRequest.usage[useCategory][useKey] = true;
				transformedWorkRequest.usage[useCategory].shipping.qty += details.quantity;
			});
			if( transformedWorkRequest.usage['theatrical'].shipping.qty ) {
				transformedWorkRequest.usage['theatrical'].shipping.qty /= Object.keys(workRequest.dcp.details).length;
			}

			// recipients
			Object.entries(workRequest?.recipients||{})?.forEach(([category, recipients]) => {
				if( recipients.length > 0 ) {
					let useCategory = category;
					if( useCategory in REVERSE_USAGE_KEY_MAP ) {
						useCategory = REVERSE_USAGE_KEY_MAP[useCategory];
					}
					transformedWorkRequest.usage[useCategory].sendTo = IdService.convertToIds(recipients);
				}
			});

			transformedWorkRequest.usage.theatrical.shipping.addresses = workRequest.shippingAddresses;
			transformedWorkRequest.usage.theatrical.shipping.addresses = setBlankAddresses(
				transformedWorkRequest.usage.theatrical
			);
		}

		return transformedWorkRequest;
	},

	/* Once a work request update has provisioned the per-language documents an
	*  edit added (a language, or a whole side worked per language for the first
	*  time), give each its translator, approval and — for an uploaded script —
	*  its file, as the create flow does: provisioning itself runs without the
	*  translation callbacks that invite a typed email, notify a vendor and mark
	*  an uploaded script submitted. Returns how many documents could not be
	*  updated, for the caller to report as a partial failure.
	* */
	applyProvisionedPerLanguageTranslations: async({ order, patch, workRequest, asset = workRequest?.asset }) => {
		const sides = [
			{
				field: 'multiTranslations',
				providerType: order.localization?.translationProviderType,
				file: order.localization?.translationFile,
				needsApproval: !!order.localization?.needsApproval
			},
			{
				field: 'graphicsMultiTranslations',
				providerType: order.localization?.graphicsTranslationProviderType,
				file: order.localization?.graphicsTranslationFile,
				needsApproval: graphicsNeedsApproval(order, asset)
			}
		];
		let failed = 0;

		for(const side of sides) {
			const sent = Array.isArray(patch?.[side.field]) ? patch[side.field] : [];
			const added = sent.filter((mapping) => !mapping.translation);
			if( added.length === 0 ) {
				continue;
			}

			const hasVendor = side.providerType === 'email';
			const newFile = side.providerType === 'upload' && side.file instanceof File ? side.file : null;

			for(const addedMapping of added) {
				const provisioned = (workRequest?.[side.field] || [])
					.find((mapping) => mapping.language === addedMapping.language);
				const translationId = provisioned?.translation?._id || provisioned?.translation;
				if( !translationId ) {
					// the update should have provisioned it: the language has no document
					captureError(new Error(`No ${addedMapping.language} translation was provisioned`));
					failed += 1;
					continue;
				}

				const update = {
					translator: addedMapping.translator,
					approval: hasVendor && side.needsApproval
				};
				try {
					if( newFile ) {
						const uploadedFile = await UploadService.uploadTranslation(
							newFile,
							{ translationId, file: newFile },
							_.noop
						);
						if( uploadedFile ) {
							update.file = uploadedFile;
							update.status = 'submitted';
						}
					}
					await TranslationService.updateTranslation(translationId, update);
				}
				catch(err) {
					captureError(err, `Failed to set up the ${addedMapping.language} translation`);
					failed += 1;
				}
			}
		}

		return { failed };
	},

	toWorkRequestPatch: async(section, order, workRequest, asset, newDueDate) => {
		let isPrintAsset = AssetService.isPrintAsset(asset);
		let isRoadshowAsset = AssetService.isRoadshowAsset(asset);
		let orderDueDateUnix = moment(order.due.dateTime).unix();
		let patchData = {};
		let translationPatchData = {};
		let graphicsTranslationPatchData = {};
		// uploads that failed while building the patch; reported to the caller
		// on the patch itself, out of band of what is sent to the API
		const uploadFailures = [];
		// PATCHes to the existing translations, applied by the caller once the
		// work request update has landed: a submission must be judged against the
		// order's new DNG (an order edited into a split one is two-sided from then
		// on), not the one it is about to leave
		const translationPatches = [];

		// Localization updated
		if( section === 'localization' ) {
			// due date
			if( orderDueDateUnix !== workRequest.due.date ) {
				patchData.due = {
					...workRequest.due,
					date: order.due.dateTime
				};
			}

			// language & territory
			if( order.localization.language !== workRequest.language ) {
				patchData.language = order.localization.language;
			}
			if( order.localization.territory !== workRequest.territory ) {
				patchData.territory = order.localization.territory;
			}

			const needsTranslation = WorkRequestOrderService.doesOrderNeedTranslation(order);
			const needsGfxTranslation = WorkRequestOrderService.doesGfxOrderNeedTranslation(order, asset);

			// edit existing translation
			if( workRequest.translation ) {
				// language
				if( patchData.language ) {
					translationPatchData.language = patchData.language;
					graphicsTranslationPatchData.language = patchData.language;
				}

				// reset all translation properties if order no longer needs translation
				if( !needsTranslation && !needsGfxTranslation ) {
					patchData.translation = null;
					patchData.status = 'submitted';

					// the documents go with the translation work, but `translator`
					// stays on the work request and grants its vendor access on its
					// own (WorkRequest::translatorsConditions), so hand the order back
					// to the requester instead of leaving the former vendor named
					if( workRequest.from?._id ) {
						patchData.translator = workRequest.from._id;
					}

					order.localization = {
						...order.localization,
						translationProvider: 'me',
						translationProviderType: null,
						translationFile: null,
						needsApproval: false,
						graphicsNeedsApproval: false,
						dubbed: false,
						audioFile: null
					};
				}

				// translator
				if( !order.localization.translationProvider ) {
					order.localization.translationProvider = workRequest.from;
				}

				const translatorId = translatorReference(order.localization.translationProvider);

				// On a split order graphics has a vendor of its own; "Built-In" on that
				// side means the requester. Everywhere else the one translator covers
				// both translations.
				const isSplit = offersSplitTranslators(order, asset);
				let graphicsTranslatorId = translatorId;
				if( isSplit ) {
					const graphicsProvider = order.localization.graphicsTranslationProviderType === 'email'
						? order.localization.graphicsTranslationProvider
						: workRequest.from;
					graphicsTranslatorId = translatorReference(graphicsProvider) || translatorId;
				}

				if( translatorId !== workRequest.translation.translator._id ) {
					translationPatchData.translator = translatorId;
				}
				if( graphicsTranslatorId !== workRequest.graphicsTranslation?.translator?._id ) {
					graphicsTranslationPatchData.translator = graphicsTranslatorId;
				}

				// The work request pins who works graphics on a split order, so a later
				// subtitle reassignment leaves graphics alone, and a change of vendor —
				// a known user or a newly typed email alike, as on create — has the API
				// re-provision the graphics script for the new vendor (who is invited
				// and resolved from the new document). Leaving split mode clears the
				// pin, so the one vendor covers both sides again.
				if( isSplit ) {
					patchData.graphicsTranslator = graphicsTranslatorId;
				}
				else if( workRequest.graphicsTranslator ) {
					patchData.graphicsTranslator = null;
				}

				// translation file — a newly picked script is uploaded and submits the
				// translation; a script already uploaded (a url) is left as it is, and the
				// file is only cleared when the side moves off the upload provider
				await applyTranslationFile(translationPatchData, {
					translationId: workRequest.translation._id,
					providerType: order.localization.translationProviderType,
					file: order.localization.translationFile,
					currentFile: workRequest.translation.file,
					label: 'translation',
					uploadFailures
				});

				// graphics translation file — a split order's graphics side has an upload of
				// its own; otherwise the one shared script covers graphics too (and a
				// dedicated upload left over from a split is cleared)
				if( workRequest.graphicsTranslation ) {
					await applyTranslationFile(graphicsTranslationPatchData, {
						translationId: workRequest.graphicsTranslation._id,
						providerType: isSplit
							? order.localization.graphicsTranslationProviderType
							: order.localization.translationProviderType,
						file: isSplit
							? order.localization.graphicsTranslationFile
							: order.localization.translationFile,
						currentFile: workRequest.graphicsTranslation.file,
						label: 'graphics translation',
						uploadFailures
					});
				}

				// per-language documents of a dual-language order: each side's mappings
				// follow the order (a side without a vendor is handed to the requester),
				// and the documents behind them are updated to match
				const orderLanguages = (order.localization.language || '').split('-').filter(Boolean);
				// the subtitle side is worked per language whenever the graphics side is,
				// as on create, so the subtitler works the order language by language
				const graphicsPerLanguage = isSplit && order.localization.graphicsTranslationProviderType === 'email';
				const perLanguageSides = [
					{
						field: 'multiTranslations',
						languages: orderLanguages,
						pairedWithPerLanguageSide: graphicsPerLanguage,
						providerType: order.localization.translationProviderType,
						orderMappings: order.localization.multiTranslations,
						file: order.localization.translationFile,
						needsApproval: order.localization.needsApproval,
						mirror: translationPatchData
					},
					...(isSplit
						? [{
							field: 'graphicsMultiTranslations',
							languages: orderLanguages,
							providerType: order.localization.graphicsTranslationProviderType,
							orderMappings: order.localization.graphicsMultiTranslations,
							file: order.localization.graphicsTranslationFile,
							needsApproval: graphicsNeedsApproval(order, asset),
							mirror: graphicsTranslationPatchData
						}]
						: [])
				];
				const perLanguageActive = usesPerLanguageTranslators(order, asset);
				if( perLanguageActive ) {
					for(const side of perLanguageSides) {
						const mappings = await applyPerLanguageTranslations(
							workRequest,
							side,
							{ uploadFailures, translationPatches }
						);
						if( mappings ) {
							patchData[side.field] = mappings;
						}
					}
				}
				// the mappings of a side that is no longer worked per language are
				// cleared, so the subtitler stops treating the order as dual-language
				// and former per-language vendors lose their access
				for(const field of['multiTranslations', 'graphicsMultiTranslations']) {
					const sideActive = perLanguageActive && (field === 'multiTranslations' || isSplit);
					if( !sideActive && (workRequest[field] || []).length > 0 ) {
						patchData[field] = [];
					}
				}

				// translation approval — only a side worked by a vendor can require it
				// (each side of a split order asks it on its own); a side handed back
				// to the requester (or an upload) drops it
				const subtitleHasVendor = order.localization.translationProviderType === 'email';
				const graphicsHasVendor = isSplit
					? order.localization.graphicsTranslationProviderType === 'email'
					: subtitleHasVendor;
				translationPatchData.approval = subtitleHasVendor && !!order.localization.needsApproval;
				graphicsTranslationPatchData.approval = graphicsHasVendor && graphicsNeedsApproval(order, asset);

				// nothing to patch when the edit removed all translation work: the
				// update deletes the documents along with it
				if( needsTranslation || needsGfxTranslation ) {
					translationPatches.push({
						side: 'translation',
						translationId: workRequest.translation._id,
						data: translationPatchData
					});
					if(workRequest.graphicsTranslation) {
						translationPatches.push({
							side: 'graphicsTranslation',
							translationId: workRequest.graphicsTranslation._id,
							data: graphicsTranslationPatchData
						});
					}
				}
			}
			// creating new translation
			else {
				if( (needsTranslation || needsGfxTranslation) && workRequest.status === 'submitted' ) {
					patchData.status = 'incomplete';
				}
				const requesterId = workRequest.from?._id;
				const addsTranslationWork = needsTranslation || needsGfxTranslation;
				// a vendor picked and then left behind when the edit drops back to no
				// translation work stays hidden in the form, so it is never sent
				if( addsTranslationWork && order.localization.translationProviderType === 'email' ) {
					patchData.translator = serializeTranslator(order.localization.translationProvider);
				}
				else if( addsTranslationWork && requesterId ) {
					// Built-In or upload: the requester works the subtitles. Name them, or
					// the API provisions the new documents for a vendor still left on the
					// work request from translation work removed earlier.
					patchData.translator = requesterId;
				}
				if( offersSplitTranslators(order, asset) ) {
					if( order.localization.graphicsTranslationProviderType === 'email' ) {
						patchData.graphicsTranslator = serializeTranslator(
							order.localization.graphicsTranslationProvider
						);
					}
					else if( requesterId ) {
						// Built-In or upload graphics: pin the side to the requester (as the
						// create flow does), so it is never provisioned for another vendor
						patchData.graphicsTranslator = requesterId;
					}
				}
				else if( addsTranslationWork && workRequest.graphicsTranslator ) {
					// no split: graphics follow the one translator again
					patchData.graphicsTranslator = null;
				}
			}

			// if new translation is type 'upload', handle uploading file after updating
			// work request, since we'll need the translation id created by the API

			if( isPrintAsset ) {
				patchData.dng = 'OV';
				patchData.printTranslation = order.localization.printTranslation;
			}
			else if( isRoadshowAsset ) {
				patchData.dng = order.localization.roadshowTranslation;
			}
			else {
				// AV asset
				patchData.dng = {
					dialogue: [order.localization.dialogue],
					graphics: [order.localization.graphics],
					narration: [order.localization.narration]
				};
			}
		}

		// Tags updated
		if( section === 'tags' ) {
			if( !(order.localization.autosubs || order.localization.auto) ) {
				patchData.tags = order.tags.tags.map((tag) => ({
					name: tag.tag,
					description: tag.instructions,
					files: tag.files.filter((file) => typeof file === 'string') // keep any existing files (URLs)
				}));
			}
		}

		// Usage updated
		if( section === 'usage' ) {
			let keys = [];

			if( isPrintAsset ) {
				keys.push('print', 'digital');
			}
			else if( isRoadshowAsset ) {
				keys.push('roadshow', 'streaming');
			}
			else {
				// AV asset
				keys.push('broadcast', 'online', 'theatrical');
			}

			let { uses, dcp, recipients, shipping } = transformUsage(_.pick(order.usage, keys));
			patchData.uses = uses;
			patchData.dcp = dcp;
			patchData.recipients = recipients;
			patchData.shippingAddresses = shipping;
		}

		// Due date pushed 24 hrs
		if( newDueDate ) {
			patchData.due = {
				...workRequest.due,
				date: newDueDate
			};
		}

		return withDeferredWork(patchData, { uploadFailures, translationPatches });
	},

	/* Applies the translation PATCHes an edit patch deferred until the work
	*  request update had landed. Each targets a side of the order rather than
	*  a document id: the update may have replaced the side's document (a
	*  language or vendor change re-provisions it), and the patch belongs to
	*  whichever document the side holds now. Returns how many failed, for the
	*  caller to report as a partial failure.
	* */
	applyTranslationPatches: async(patch, updatedWorkRequest) => {
		let failed = 0;

		for(const { side, field, language, translationId, data } of patch?.[TRANSLATION_PATCHES] || []) {
			// the document a side (or a side's language) holds after the update;
			// one the update dropped has nothing left to patch
			let current;
			if( field ) {
				const mapping = (updatedWorkRequest?.[field] || []).find((entry) => entry.language === language);
				current = mapping?.translation?._id || mapping?.translation;
			}
			else {
				current = updatedWorkRequest?.[side]?._id;
			}
			if( updatedWorkRequest && !current ) {
				continue;
			}
			const target = current || translationId;
			// A document the update re-provisioned (a new vendor, a new language) is a
			// fresh script its vendor has yet to work. The API mirrors a submitted
			// order's status onto it, so put it back to incomplete — otherwise an
			// approval set now would leave it submitted with nothing left to approve.
			const reprovisioned = target !== translationId;
			const update = reprovisioned && !data.status
				? { ...data, status: 'incomplete' }
				: data;
			try {
				await TranslationService.updateTranslation(target, update);
			}
			catch(err) {
				captureError(err, `Failed to update translation ${translationId}`);
				failed += 1;
			}
		}

		return { failed };
	}
};

/* Records on the patch object itself, as non-enumerable properties that never
*  reach the API, the work the caller finishes once the update has landed:
*  the translation PATCHes to apply, and the uploads that failed while the
*  patch was built, to report as a partial failure.
* */
export const UPLOAD_FAILURES = 'uploadFailures';
export const TRANSLATION_PATCHES = 'translationPatches';
function withDeferredWork(patchData, { uploadFailures, translationPatches }) {
	if( uploadFailures.length > 0 ) {
		Object.defineProperty(patchData, UPLOAD_FAILURES, { value: uploadFailures, enumerable: false });
	}
	if( translationPatches.length > 0 ) {
		Object.defineProperty(patchData, TRANSLATION_PATCHES, { value: translationPatches, enumerable: false });
	}
	return patchData;
}

// Used for checking if an order's field is pristine
const BLANK_ORDER = WorkRequestOrderService.createBlankOrder();

const BLANK_PARTIALS = {
	tags: createTag()
};

const USAGE_KEY_MAP = {
	Roadshow: 'Dcp',
	Theatrical: 'Dcp',
	Streaming: 'Streamingfile',
	Digital: 'Rgb',
	Print: 'Cmyk'
};

const REVERSE_USAGE_KEY_MAP = {
	'2D Flat': 'theatrical',
	'2D Flat 5_1': 'theatrical',
	'2D Scope': 'theatrical',
	'2D Scope 5_1': 'theatrical',
	'2D Combo': 'theatrical',
	'3D Flat': 'theatrical',
	'3D Flat 5_1': 'theatrical',
	'3D Scope': 'theatrical',
	'3D Scope 5_1': 'theatrical',
	'IMAX Laser 5_0': 'theatrical',
	'IMAX Laser 12_0': 'theatrical',
	'IMAX Xenon 5_0': 'theatrical',
	'IMAX Xenon 12_0': 'theatrical',
	'3D IMAX Laser 5_0': 'theatrical',
	'3D IMAX Laser 12_0': 'theatrical',
	'3D IMAX Xenon 5_0': 'theatrical',
	'3D IMAX Xenon 12_0': 'theatrical',
	'2D Scope Atmos': 'theatrical',
	'2D Flat Atmos': 'theatrical',
	'3D Scope Atmos': 'theatrical',
	'3D Flat Atmos': 'theatrical',
	'2D Flat Dolby Vision (Atmos)': 'theatrical',
	'2D Scope Dolby Vision (Atmos)': 'theatrical',
	'3D Flat Dolby Vision (Atmos)': 'theatrical',
	'3D Scope Dolby Vision (Atmos)': 'theatrical',
	Broadcast: 'broadcast',
	Online: 'online',
	dcp: 'theatrical',
	streamingfile: 'streaming',
	rgb: 'digital',
	cmyk: 'print'
};

function createTag() {
	return {
		tag: '',
		instructions: '',
		files: []
	};
}

function transformUsage(usage) {
	let uses = {
		items: [],
		details: {}
	};
	let dcp = {
		items: [],
		details: {}
	};
	let recipients = {};
	let shipping = {};

	// Defensive payload cleanup: strip invalid recipients (empty strings,
	// whitespace-only strings, or structureless objects) that may linger in
	// state before serializing to the API.
	const sanitizeSendTo = (sendTo) => (sendTo || []).filter((entry) => {
		if( typeof entry === 'string' ) {
			return entry.trim().length > 0;
		}
		if( entry && typeof entry === 'object' ) {
			return !!(entry.id || entry._id);
		}
		return false;
	});

	Object.entries(usage||{})?.forEach(([category, use]) => {
		let useKey = _.capitalize(category);

		if( useKey in USAGE_KEY_MAP ) {
			useKey = USAGE_KEY_MAP[useKey];
		}

		// Get all selected usage
		let values = _.keys(use)?.filter((usage) => {
			return ['sendTo', 'shipping', 'deliveryDeadline'].indexOf(usage) < 0 && use[usage];
		});

		// If values have been selected, add them to the work order
		if( values.length ) {
			const cleanSendTo = sanitizeSendTo(use.sendTo);
			if( useKey === 'Dcp' && category !== 'roadshow' ) {
				values.forEach((dcpItem) => {
					let qty = parseInt(use.shipping.qty);

					dcp.items.push(dcpItem);
					dcp.details[dcpItem] = {
						link: true,
						ship: qty > 0,
						quantity: qty
					};
					recipients[dcpItem] = IdService.convertFromIds(cleanSendTo);
					if( use.shipping.qty ) {
						shipping = use.shipping.addresses;
					}
				});
			}
			else {
				uses.items.push(useKey);
				uses.details[useKey] = values?.map((value) => ({
					value: value,
					// TODO: Shipping
					ship: false
				})) ?? [];
				if( cleanSendTo.length ) {
					recipients[useKey] = IdService.convertFromIds(cleanSendTo);
				}
			}
		}
	});

	return {
		uses,
		dcp,
		recipients,
		shipping
	};
}

/* Whether every per-language record names the given user as its translator. */
function allWorkedBy(multiTranslations, user) {
	const userId = user?._id ?? user?.id;
	return !!userId && multiTranslations.every((entry) => {
		const translatorId = entry?.translator?._id ?? entry?.translator?.id ?? entry?.translator;
		return String(translatorId) === String(userId);
	});
}

function getTranslationProviderType(workRequest) {
	let { translation, from } = workRequest;
	let isUserTranslator = translation.translator._id === from._id;

	if( !isUserTranslator ) {
		return 'email';
	}

	// If the work request has multiple translators, use the email translator (My Subtitling Vendor),
	// unless every language is worked by the requester (per-language records only exist so the
	// graphics side could be split per language).
	if(workRequest?.multiTranslations?.length > 0 && !allWorkedBy(workRequest.multiTranslations, from)) {
		return 'email';
	}

	if( translation.file ) {
		return 'upload';
	}

	return 'me';
}

/* Whether the graphics side of a split order is worked by the requester, a
*  vendor, or came in as an uploaded script.
* */
function getGraphicsTranslationProviderType(workRequest) {
	const { graphicsTranslation, from } = workRequest;
	const translatorId = graphicsTranslation?.translator?._id ?? graphicsTranslation?.translator;

	if( translatorId && translatorId !== from?._id ) {
		return 'email';
	}

	// One graphics translator per language is the vendor option too (My Graphics Vendor)
	if( workRequest.graphicsMultiTranslations?.length > 0
		&& !allWorkedBy(workRequest.graphicsMultiTranslations, from)
	) {
		return 'email';
	}

	return graphicsTranslation?.file ? 'upload' : 'me';
}

/* Brings one side's per-language documents of an existing dual-language
*  order in line with the order: one mapping per language of the order, its
*  translator the vendor picked for it or the requester when the side names
*  no vendor. A language that already has a document keeps it (and has its
*  translator and approval updated, and a newly picked script uploaded to
*  it, recorded on the order-wide document too since that is what the order
*  form reads back); a language that has none — a newly added language, or
*  a side that was never split per language — is sent without a document id
*  for the API to provision.
*
*  A side is only worked per language when it names vendors, already has
*  per-language documents (which then follow the order, or go back to the
*  requester), or is the subtitle side of an order whose graphics are per
*  language; otherwise it stays on its order-wide document, as on create, and
*  null is returned so nothing is provisioned.
* */
async function applyPerLanguageTranslations(workRequest, side, { uploadFailures, translationPatches }) {
	const currentMappings = workRequest[side.field] || [];
	if( !side.languages?.length ) {
		return null;
	}

	const hasVendor = side.providerType === 'email';
	if( !hasVendor && currentMappings.length === 0 && !side.pairedWithPerLanguageSide ) {
		return null;
	}
	const requesterId = workRequest.from?._id || workRequest.from?.id;
	const newFile = side.providerType === 'upload' && side.file instanceof File ? side.file : null;

	const mappings = side.languages.map((language) => {
		const current = currentMappings.find((mapping) => mapping.language === language);
		const currentTranslation = current?.translation?._id || current?.translation;
		const picked = hasVendor
			? (side.orderMappings || []).find((entry) => entry.language === language)?.translator
			: null;
		return {
			language,
			translator: (picked && translatorReference(picked)) || requesterId,
			...(currentTranslation ? { translation: currentTranslation } : {})
		};
	});

	const wantedApproval = hasVendor && !!side.needsApproval;

	await Promise.all(mappings.filter((mapping) => !!mapping.translation).map(async(mapping) => {
		// Only what actually changed is PATCHed: every save of a per-language
		// document re-sends its vendor's request email, so an unrelated
		// localization edit must leave the documents alone.
		const document = await loadTranslation(mapping.translation);
		const update = {};
		if( !document || translatorReference(document.translator) !== mapping.translator ) {
			update.translator = mapping.translator;
		}
		if( !document || !!document.approval !== wantedApproval ) {
			update.approval = wantedApproval;
		}
		if( newFile ) {
			try {
				const uploadedFile = await UploadService.uploadTranslation(
					newFile,
					{ translationId: mapping.translation, file: newFile },
					_.noop
				);
				if( uploadedFile ) {
					update.file = uploadedFile;
					update.status = 'submitted';
					side.mirror.file = side.mirror.file ?? uploadedFile;
				}
			}
			catch(err) {
				captureError(err, `Failed to upload ${mapping.language} translation file`);
				uploadFailures?.push({ translationId: mapping.translation, label: mapping.language, error: err });
			}
		}
		// Applied only once the work request update has landed (see
		// applyTranslationPatches): a vendor is never invited or notified for an
		// edit that then fails. An invited email is resolved by the API from the
		// document, which writes the resolved id back onto the mapping.
		if( Object.keys(update).length > 0 ) {
			translationPatches.push({
				field: side.field,
				language: mapping.language,
				translationId: mapping.translation,
				data: update
			});
		}
	}));

	return mappings;
}

/* The current state of a translation document, or null when it cannot be
*  read — in which case its caller patches it in full rather than guess.
* */
async function loadTranslation(translationId) {
	try {
		const document = await TranslationService.getById(translationId);
		return document && !_.isError(document) ? document : null;
	}
	catch(err) {
		captureError(err, `Failed to load translation ${translationId}`);
		return null;
	}
}

/* Applies an order's translation-file choice to the patch of an existing
*  translation. A newly picked File is uploaded and, once it is up, submits the
*  translation; a failed upload changes nothing and is reported. A script that
*  is already uploaded arrives as its url: left alone when it is the one the
*  translation already holds, adopted (as a finished translation) when it is
*  another — the shared script reaching graphics once a split order is merged
*  back. Only a side that moved off the upload provider has its file cleared.
* */
async function applyTranslationFile(patchData, {
	translationId, providerType, file, currentFile, label, uploadFailures
}) {
	if( providerType !== 'upload' ) {
		patchData.file = null;
		return;
	}

	if( typeof file === 'string' ) {
		if( file && file !== currentFile ) {
			patchData.file = file;
			patchData.status = 'submitted';
		}
		return;
	}

	if( !(file instanceof File) ) {
		return;
	}

	try {
		const uploadedFile = await UploadService.uploadTranslation(file, { translationId, file }, _.noop);
		if( uploadedFile ) {
			patchData.file = uploadedFile;
			patchData.status = 'submitted';
		}
	}
	catch(err) {
		captureError(err, `Failed to upload ${label} file`);
		uploadFailures?.push({ translationId, label, error: err });
	}
}

/* What an existing translation's `translator` is compared against and patched
*  with: the user id of a known user, or the email typed for one still to be
*  invited. Mirrors `serializeTranslator()` for the order-edit path, where
*  users arrive keyed by `_id` as well as `id`.
* */
function translatorReference(provider) {
	if(typeof provider === 'string') {
		return provider;
	}
	return provider?._id || provider?.id || undefined;
}

/* A DNG component of a work request: a one-item list, or a bare string on
*  older orders.
* */
function dngComponent(workRequest, name) {
	const value = workRequest?.dng?.[name];
	return Array.isArray(value) ? value[0] : value;
}

/* The value the API takes for a translator on a work request: the user id of an
*  existing user, or the email of a user that has yet to be invited (the API's
*  NormalizeAndInvite resolves that to a user later). Anything else is dropped.
* */
function serializeTranslator(translator) {
	if(typeof translator === 'string') {
		return translator;
	}
	if(translator?.id) {
		return translator.id;
	}
	if(translator?.email || translator?.emailNormal) {
		return translator.email ?? translator.emailNormal;
	}
	return undefined;
}

function setBlankAddresses(field) {
	field.sendTo.forEach((user) => {
		// sendTo may contain bare ID strings when recipients aren't populated;
		// only populated user objects have a name to key a blank address on.
		if( !user || typeof user !== 'object' ) {
			return;
		}
		if( !Object.prototype.hasOwnProperty.call(field.shipping.addresses, user.name) ) {
			field.shipping.addresses[user.name] = {
				name: '$links' in user ? user.name : '',
				address: '',
				city: '',
				state: '',
				postalCode: '',
				country: ''
			};
		}
	});

	return field.shipping.addresses;
}

export default WorkRequestOrderService;
